import { useState } from 'react';
import { X, LoaderCircle } from 'lucide-react';
import { useCreateClass } from '../../api/class/classQueries';
import { useGetAllSessions } from '../../api/session/sessionQueries';
import { useGetAllStaff } from '../../api/staff/staffQueries';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { LoadingSpinner } from '../ui/loading-spinner.tsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialogue.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select.tsx';

export default function ClassModal({ isOpen, onClose, classToEdit = null }) {
  // State for form data
  const [formData, setFormData] = useState({
    className: classToEdit?.className || '',
    sessionId: classToEdit?.sessionId || '',
    formStaffId: classToEdit?.formStaffId || ''
  });

  // API hooks
  const { data: sessionsData, isLoading: isLoadingSessions } = useGetAllSessions();
  const { data: staffData, isLoading: isLoadingStaff } = useGetAllStaff();
  const { mutate: createClass, isPending } = useCreateClass();

  // Format sessions data
  const sessions = sessionsData?.data || sessionsData || [];

  // Format staff data
  const staff = staffData?.data || staffData || [];
  
  // Handle input change
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Simple validation
    if (!formData.className || !formData.sessionId || !formData.formStaffId) {
      return;
    }

    createClass(formData, {
      onSuccess: () => {
        onClose();
        setFormData({
          className: '',
          sessionId: '',
          formStaffId: ''
        });
      }
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{classToEdit ? 'Edit Class' : 'Add New Class'}</DialogTitle>
          <button
            onClick={onClose}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="className">Class Name</Label>
            <Input
              id="className"
              placeholder="e.g. JSS3A, SS1B"
              value={formData.className}
              onChange={(e) => handleChange('className', e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="session">Academic Session</Label>
            <Select
              value={formData.sessionId}
              onValueChange={(value) => handleChange('sessionId', value)}
              required
            >
              <SelectTrigger id="session" className="w-full">
                <SelectValue placeholder={isLoadingSessions ? "Loading sessions..." : "Select session"} />
              </SelectTrigger>
              <SelectContent>
                {isLoadingSessions ? (
                  <div className="flex items-center justify-center p-2">
                    <LoaderCircle className="h-4 w-4 animate-spin" />
                    <span className="ml-2">Loading...</span>
                  </div>
                ) : (
                  sessions.map((session) => (
                    <SelectItem key={session.sessionId} value={session.sessionId}>
                      {session.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="formStaff">Form Teacher</Label>
            <Select
              value={formData.formStaffId}
              onValueChange={(value) => handleChange('formStaffId', value)}
              required
            >
              <SelectTrigger id="formStaff" className="w-full">
                <SelectValue placeholder={isLoadingStaff ? "Loading staff..." : "Select form teacher"} />
              </SelectTrigger>
              <SelectContent>
                {isLoadingStaff ? (
                  <div className="flex items-center justify-center p-2">
                    <LoaderCircle className="h-4 w-4 animate-spin" />
                    <span className="ml-2">Loading...</span>
                  </div>
                ) : (
                  staff.map((teacher) => (
                    <SelectItem key={teacher.staffId || teacher.id} value={teacher.staffId || teacher.id}>
                      {teacher.firstName && teacher.lastName 
                        ? `${teacher.firstName} ${teacher.lastName}`
                        : teacher.name || 'Unknown'}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-purple-600 hover:bg-purple-700"
              disabled={isPending}
            >
              {isPending ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  {classToEdit ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                classToEdit ? 'Update Class' : 'Create Class'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}